import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import MessageRow from "./MessageRow";
import MessageDetail from "./MessageDetail";
import { Button } from "@mui/material";
import OutboxOutlinedIcon from '@mui/icons-material/OutboxOutlined';
import { Spinner, ErrorBanner, EmptyState, inputStyle, secondaryBtn } from "./MessagesCommon";
import { PROXY_GET_OUTBOX_MESSAGES, PROXY_GET_SPECIFIC_MESSAGE } from "../config/settings";

export default function MessageList() {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [selected, setSelected] = useState(null);
  const [filterStatus, setFilterStatus] = useState("");
  const [filterType, setFilterType] = useState("");
  const LIMIT = 25;

  const fetchMessages = useCallback(async (newOffset = 0, replace = false) => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ limit: LIMIT, offset: newOffset });
      if (filterStatus) params.set("status", filterStatus);
      if (filterType) params.set("messageType", filterType);

      // Hardcoded strictly to outbox endpoint strings per the guide
      const path = `${PROXY_GET_OUTBOX_MESSAGES}?${params.toString()}`;
      const { data } = await axios.get(path);

      if (data?.data.length>0) {
        const rows = data.data ?? [];
        setMessages((prev) => replace ? rows : [...prev, ...rows]);
        setHasMore(rows.length === LIMIT);
        setOffset(newOffset + rows.length);
      } else {
        setError(data.message || "Failed to load broadcast messages.");
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [filterStatus, filterType]);

  useEffect(() => {
    setMessages([]);
    setOffset(0);
    setHasMore(true);
    fetchMessages(0, true);
  }, [filterStatus, filterType]);

  const handleOpen = async (msg) => {
    try {
      // Pull outbound detail log context explicitly
      const { data } = await axios.get(PROXY_GET_SPECIFIC_MESSAGE(msg.id, "outbox"));

      if (data.status === "SUCCESS") {
        setSelected(data.data);
      } else {
        setSelected(msg);
      }
    } catch {
      setSelected(msg);
    }
  };

  // Restructured to support broadcast targets only
  const statusOptions = ["", "SENT", "DELIVERED", "FAILED"];

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <div style={{ padding: "12px 20px", borderBottom: "1px solid #f0f0f4", display: "flex", gap: "8px", flexWrap: "wrap" }}>
        <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} style={{ ...inputStyle, width: "auto", padding: "6px 10px" }}>
          {statusOptions.map((s) => <option key={s} value={s}>{s || "All statuses"}</option>)}
        </select>
        <select
          value={filterType}
          onChange={(e) => setFilterType(e.target.value)}
          style={{ ...inputStyle, width: "160px", padding: "6px 10px" }}
        >
          <option value="">All Types</option>
          <option value="GENERIC">GENERIC</option>
          <option value="ALERT">ALERT</option>
        </select>
        <button
          onClick={() => {
            setFilterStatus(""); setFilterType("");
          }}
          style={{ ...secondaryBtn, padding: "6px 12px", fontSize: "13px" }}
        >
          Clear filters
        </button>
      </div>

      <div style={{ flex: 1, overflow: "auto" }}>
        {error && <div style={{ padding: "12px 20px" }}><ErrorBanner message={error} /></div>}
        {!loading && messages.length === 0 && !error && (
          <EmptyState
            icon= {<OutboxOutlinedIcon/>}
            title="Nothing sent yet"
            body="Broadcast messages will appear here once they are dispatched."
          />
        )}
        {messages.map((msg) => (
          <MessageRow key={msg.id} msg={msg} onOpen={handleOpen} />
        ))}
        {loading && (
          <div style={{ display: "flex", justifyContent: "center", padding: "24px" }}>
            <Spinner />
          </div>
        )}
        {hasMore && !loading && messages.length > 0 && (
          <div style={{ padding: "16px 20px", textAlign: "center" }}>
            <Button
              onClick={() => fetchMessages(offset, false)}
              style={{ ...secondaryBtn, fontSize: "13px" }}
            >
              Load more
            </Button>
          </div>
        )}
      </div>

      {selected && (
        <MessageDetail
          msg={selected}
          onClose={() => setSelected(null)}
        />
      )}
    </div>
  );
}
